import { defineAsyncComponent } from "vue";
import { mapState, mapGetters, mapActions } from "vuex";
import { isDefined } from "@/utils/var.check";
import {
  NAMESPACE,
  ADD_COMPONENT_TYPE,
  ADD_FROM_LIBRARY_SEARCH_MODE,
  ADD_FROM_EXISTING_TYPE,
  ADD_FROM_EXISTING_LOCATION,
  VALIDATION,
  THUMBNAIL_RESOURCE_TYPE,
  COURSE_CREATION_OPTION,
} from "@modules/editor/service/editor.constant";
import { CroppingToolKind } from "@plugins/cropping/cropping.constant";
import MediaObjectApi from "@apis/media.object.api";
import LessonApi from "@apis/editor.lesson.api";
import { ACTIVITY_TYPE_SELECTION } from "../service/editor.constant";
import {
  LESSON_ACTIVITY_TYPE,
  RESOURCE_DOMAIN,
} from "@plugins/common/constant/resource.constant.ts";
import i18n from "@plugins/i18n";
import { isEmpty, isNull } from "@utils/var.check";
import quizNavigatorStore from "@plugins/quiz-navigator/components/QuizNavigatorStore";
import SearchModuleDetailContentSubpage from "@modules/search/components/SearchModuleDetailContentSubpage.vue";
import { FileStatus, UploadFlow, Dimensions } from "@plugins/file-uploader-v2/types.ts";

/**
 * This mixin is used to add resources (Activity, Question, Rubric, Quiz, Media) inside lesson.
 * This is being used by the following subpages:
 * 1. AddFromLibraryContentSubpage
 * 2. AddFromStarredResourcesContentSubpage
 * 3. AddActivityContentSubpage
 */
export default {
  emits: ["selectionSaved", "onMediaSelected", "activityAdded", "rubricSelected"],
  data() {
    return {
      parentSubpageName: "",
      isProcessing: { data: false },
      // used by quiz patch mixin
      resourceUuid: "",
      lockService: null,
      copyFunction: null,
      lessonTitle: null,
      packageType: null,
      quizNavigatorStore: quizNavigatorStore,
    };
  },
  computed: {
    ...mapState(NAMESPACE.LESSON, {
      lesson: (state) => state.lesson,
    }),
    ...mapGetters(NAMESPACE.LESSON, [
      "currentPageId",
      "getActivity",
      "getActivityList",
      "currentSectionId",
      "currentSection",
      "getQuizAlpStageId",
      "getLeafComponentsInActivity",
    ]),
    backLabel() {
      return `${this.$i18n.t("content_subpage_go_back_link")} ${this.parentSubpageName}`;
    },
    fromSourceBackLabel() {
      return this.$i18n.t("calendar_event_back_to_attach_assignment_label");
    },
    isDisabled() {
      return this.isProcessing.data || !this.isComponentDirty();
    },
    showTooltip() {
      return this.params.addFromType === ADD_FROM_EXISTING_TYPE.ACTIVITY;
    },
    showActionButton() {
      return (
        this.params.mode === ADD_FROM_LIBRARY_SEARCH_MODE.MULTI_SELECTION ||
        this.params.mode === ADD_FROM_LIBRARY_SEARCH_MODE.MULTI_SELECTION_COMPONENT ||
        (this.params?.addFromType === ADD_COMPONENT_TYPE.RUBRIC &&
          this.params.addFromLocation === ADD_FROM_EXISTING_LOCATION.LIBRARY)
      );
    },
    isAddFromlibraryOrStarredResources() {
      return this.params.addFromLocation === ADD_FROM_EXISTING_LOCATION.STARRED_RESOURCES;
    },
    addFromTypeLabel() {
      switch (this.params.addFromType) {
        case ADD_FROM_EXISTING_TYPE.ACTIVITY:
          return this.$i18n.t("add_existing_activity_title");
        default:
          return "";
      }
    },
    addFromLocationLabel() {
      switch (this.params.addFromLocation) {
        case ADD_FROM_EXISTING_LOCATION.LIBRARY:
          return this.$i18n.t("add_existing_from_library_title");
        case ADD_FROM_EXISTING_LOCATION.STARRED_RESOURCES:
          return this.$i18n.t("add_existing_from_starred_resources_title");
        default:
          return "";
      }
    },
  },
  methods: {
    ...mapActions(NAMESPACE.LESSON, [
      "addThumbnail",
      "uploadThumbnail",
      "resetEditingComponentId",
      "saveDuplicateComponent",
      "saveDuplicateMultipleComponent",
      "saveQuizActivityTitle",
      "setLesson",
      "copyPages",
      "copyPagesToSection",
    ]),
    ...mapActions(NAMESPACE.COMPONENT, [
      "addQuestion",
      "duplicateQuestion",
      "resetEditingResourceUuid",
      "setEditingResourceUuid",
    ]),
    getSelectionByUuid() {
      return Object.keys(this.store.getSelectionList())[0];
    },
    getSelectionListByUuid() {
      return Object.keys(this.store.getSelectionList())
        .map((uuid) => {
          return uuid;
        })
        .reduce(function (filtered, data) {
          filtered.push(data);
          return filtered;
        }, []);
    },
    getQuestionPartsSelectionListByUuid() {
      return Object.keys(this.store.getQuestionPartsSelectionList())
        .map((uuid) => {
          return uuid;
        })
        .reduce(function (filtered, data) {
          filtered.push(data);
          return filtered;
        }, []);
    },
    getFilteredSelection() {
      return Object.values(this.store.getSelectionList())
        .map((data) => {
          return data;
        })
        .reduce(function (filtered, data) {
          filtered.push(data);
          return filtered;
        }, []);
    },
    handleSelectionChanged() {
      const selection = Object.entries(this.store.getSelectionList()).length;
      if (selection > 0) {
        this.setDirty(this.dirtyId);
      } else {
        this.removeDirty(this.dirtyId);
      }
    },
    handleSelected(data) {
      if (this.params.isAttachingAttachment) {
        this.$contentSubpage.show(
          SearchModuleDetailContentSubpage,
          {
            baseTitle: [...this.titles],
            hasParentSubpage: true,
            type: data.type,
            uuid: data.uuid,
            resourceDomain: data.resourceDomain,
            backLabelFromParent: this.fromSourceBackLabel,
            isAttachingAttachment: this.params.isAttachingAttachment,
            showHeader: true,
            isSelectingResource: true,
          },
          {
            selectionSelected: (sectionSelected) => {
              this.$emit("selectionSaved", {
                selectedValue: data.uuid,
                sectionId: sectionSelected,
              });
              setTimeout(() => {
                this.actionBack();
              }, 100);
            },
          },
        );
      }
    },
    handleSelectionSaved(data) {
      if (this.params.createNewAssignment || this.params.isFromPlanner) {
        this.isProcessing.data = true;
        this.$emit("selectionSaved", data);
        if (this.params.isFromPlanner) {
          this.$nextTick(() => {
            this.actionBack();
          });
        }
        this.isProcessing.data = false;
      } else if (!this.isProcessing.data) {
        this.isProcessing.data = true;
        switch (this.params.addFromType) {
          case ADD_FROM_EXISTING_TYPE.QUESTION:
            this.handleSaveQuestion(data);
            break;
          case ADD_FROM_EXISTING_TYPE.RUBRIC:
            this.handleGradingRubricSelected(data);
            break;
          case ADD_FROM_EXISTING_TYPE.MEDIA:
            if (this.params.allowUpload(this.getFilteredSelection()[0].type)) {
              this.handleSaveMediaObject({
                ...data,
                isAddNewMedia: true,
              });
            } else {
              this.showMaximumUploadModal();
              this.store.clearSelectionList();
            }
            break;
          case ADD_FROM_EXISTING_TYPE.THUMBNAIL:
            this.handleSaveThumbnail(data);
            break;
          case ADD_FROM_EXISTING_TYPE.ACTIVITY:
            this.copyFunction = this.handleSaveActivities.bind(this, data);
            this.handleSaveActivities(data);
            break;
          case ADD_FROM_EXISTING_TYPE.GAME_BOOK:
            this.handleSelectGameBook(data);
            break;
          default:
            this.isProcessing.data = false;
            break;
        }
      }
    },
    handleSelect() {
      if (this.params?.addFromType === ADD_COMPONENT_TYPE.QUESTION) {
        this.handleSaveQuestion();
      } else if (this.params?.addFromType === ADD_COMPONENT_TYPE.RUBRIC) {
        const selection = Object.values(this.store.getSelectionList())[0];
        this.removeDirty(this.dirtyId);
        this.handleGradingRubricSelected({
          data: { uuid: selection.uuid },
        });
      }
    },
    handleGradingRubricSelected(event) {
      // handle selected from search resource detail content subpage to include closing of subpages
      if (event.onSuccessCallback) {
        event.onSelectedCallBack = () => {
          this.$nextTick(() => {
            this.actionBack();
          });
        };
      }
      this.$emit("rubricSelected", event);
    },
    handleSaveQuestion(data) {
      this.isProcessing.data = true;
      if (this.params.mode === ADD_FROM_LIBRARY_SEARCH_MODE.SINGLE_SELECTION) {
        this.saveSingleQuestion(data);
      } else if (this.params.mode === ADD_FROM_LIBRARY_SEARCH_MODE.MULTI_SELECTION) {
        // Has restriction in MPQ and has to validate before save
        const isValid = this.validateMPQChildSelection() && this.validateMPQSubSelection();
        if (isValid) this.saveMultipleQuestion(data);
      } else if (this.params.mode === ADD_FROM_LIBRARY_SEARCH_MODE.MULTI_SELECTION_COMPONENT) {
        this.saveMultipleQuestionComponent(data);
      }
    },
    validateMPQChildSelection() {
      if (!this.params.addFromMpqChildLayer) {
        return true;
      }

      let questionPartSelectionLength = 0;
      questionPartSelectionLength = Object.entries(
        this.store.getQuestionPartsSelectionList(),
      ).length;
      const totalChildQuestions =
        this.params.childQuestionCount +
        Object.entries(this.store.getSelectionList()).length +
        questionPartSelectionLength;
      if (totalChildQuestions > VALIDATION.MAX_CHILD_QUESTION) {
        this.showMPQMaxChildQuestion();
        this.isProcessing.data = false;
        return false;
      }
      return true;
    },
    showMPQMaxChildQuestion() {
      this.$modal.showMessage("warning", {
        title: this.$i18n.t("question_mpq_label_add_question"),
        content: this.$i18n.t("question_mpq_max_child_error_message", {
          maxChild: VALIDATION.MAX_CHILD_QUESTION,
        }),
        primaryBtnLabel: this.$i18n.t("button_ok"),
        use_secondaryButton: false,
        actionPrimary: () => {
          // empty
        },
        actionHidden: () => {
          // empty
        },
      });
    },
    validateMPQSubSelection() {
      if (!this.params.addFromMpqSubLayer) {
        return true;
      }

      const totalSubQuestions =
        this.params.subQuestionCount +
        Object.entries(this.store.getSelectionList()).length +
        +Object.entries(this.store.getQuestionPartsSelectionList()).length;
      if (totalSubQuestions > VALIDATION.MAX_TOTAL_SUB_QUESTION) {
        this.$modal.showMessage("warning", {
          title: this.$i18n.t("question_mpq_label_add_question"),
          content: this.$i18n.t("question_mpq_max_total_sub_question_error_message", {
            maxSubQuestion: VALIDATION.MAX_TOTAL_SUB_QUESTION,
          }),
          primaryBtnLabel: this.$i18n.t("button_ok"),
          use_secondaryButton: false,
          actionPrimary: () => {
            // empty
          },
          actionHidden: () => {
            // empty
          },
        });
        this.isProcessing.data = false;
        return false;
      }
      return true;
    },
    validateHasReachedMaximumNoOfComponents(questionUuids) {
      const hasReachedMaximumNoOfComponents =
        questionUuids.length + this.getLeafComponentsInActivity(this.currentPageId).length >
        VALIDATION.MAX_COMPONENTS;

      if (hasReachedMaximumNoOfComponents) {
        this.dataChooseButton.loading = false;
        this.dataChooseButton.error = true;
        this.dataChooseButton.errorMessage = this.$i18n.t("lesson_editor_maximum_components_error");
        this.isProcessing.data = false;

        return false;
      }
      return true;
    },
    handleChooseButtonResetError() {
      this.dataChooseButton.error = false;
      this.dataChooseButton.loading = false;
    },
    handleChooseButtonResetCompleted() {
      this.dataChooseButton.completed = false;
      this.dataChooseButton.loading = false;
    },
    saveSingleQuestion(data) {
      let containerId = 0;
      const activityType = this.getActivity(this.currentPageId).type;
      let indexNo = this.params.indexNo;

      if (
        activityType === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ &&
        this.params.containerId !== "newContainer"
      ) {
        containerId = this.params.containerId;
      } else {
        containerId = this.params.isRootColumnSingle ? "" : this.params.containerId;
      }

      if (activityType === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ && containerId === "") {
        indexNo = this.quizNavigatorStore.getCurrentPage(this.currentPageId);
      }

      const questionData = {
        componentId: "",
        containerId: containerId,
        indexNo: indexNo,
        metadata: null,
        pageId: this.currentPageId,
        resourceUuid: this.getSelectionByUuid(),
        type: "COMPONENT_QUESTION",
      };
      this.saveDuplicateComponent({ uuid: this.lesson.uuid, params: questionData })
        .then(
          (response) => {
            if (isDefined(data) && response && typeof data.onSuccessCallback === "function") {
              data.onSuccessCallback();
            }
            // Save question
            this.removeDirty(this.dirtyId);
            this.actionHidden();
            if (this.params.isRootColumnSingle && this.params.containerId === "") {
              this.resetEditingComponentId();
            }
            this.store.clearSelectionList();
          },
          () => {
            if (isDefined(data) && typeof data.onFailCallback === "function") {
              data.onFailCallback();
            }
          },
        )
        .finally(() => {
          this.isProcessing.data = false;
        });
    },
    saveMultipleQuestionComponent(data) {
      const questionUuids = !isEmpty(this.store.getQuestionPartsSelectionList())
        ? this.getQuestionPartsSelectionListByUuid()
        : this.getSelectionListByUuid();

      if (!this.validateHasReachedMaximumNoOfComponents(questionUuids)) {
        return;
      }

      let containerId = 0;
      const activityType = this.getActivity(this.currentPageId).type;
      let indexNo = this.params.indexNo;

      if (this.params.containerId === "newContainer") {
        containerId = this.params.isRootColumnSingle ? "" : this.params.containerId;
      } else {
        containerId = this.params.containerId;
      }

      if (activityType === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ && containerId === "") {
        indexNo = this.quizNavigatorStore.getCurrentPage(this.currentPageId);
      }

      const requestData = {
        componentId: "",
        lessonUuid: this.lesson.uuid,
        containerId: containerId,
        startIndexNo: indexNo,
        pageId: this.currentPageId,
        questionUuids: questionUuids,
        lessonActivityType: activityType,
      };

      this.saveDuplicateMultipleComponent({ params: requestData })
        .then(
          (response) => {
            if (isDefined(data) && response && typeof data.onSuccessCallback === "function") {
              data.onSuccessCallback();
            }
            // Save question
            this.removeDirty(this.dirtyId);
            this.actionHidden();
            if (this.params.isRootColumnSingle && this.params.containerId === "") {
              this.resetEditingComponentId();
            }
            this.store.clearSelectionList();
            this.store.clearQuestionPartsSelectionList();
          },
          () => {
            if (isDefined(data) && typeof data.onFailCallback === "function") {
              data.onFailCallback();
            }
          },
        )
        .finally(() => {
          this.isProcessing.data = false;
        });
    },
    saveMultipleQuestion(data) {
      // Handle saving multiple question inside MPQ
      let questionList = [];
      if (isEmpty(this.getSelectionListByUuid())) {
        const questionUuids = this.store.getQuestionPartsSelectionList();
        for (const question in questionUuids) {
          questionList.push(question);
        }
      } else {
        questionList = this.getSelectionListByUuid();
      }
      const questionData = {
        contextType: "LESSON",
        contextUuid: this.lesson.uuid,
        parentQuestionUuid: this.params.parentQuestionUuid,
        startingIndex: this.params.indexNo,
        uuids: questionList,
        isInQuiz: this.params.isInQuiz,
        sectionId: this.currentSection.id,
      };
      const parentComponentId = this.params.parentComponentId;
      this.duplicateQuestion(questionData)
        .then(
          () => {
            if (isDefined(data) && typeof data.onSuccessCallback === "function") {
              data.onSuccessCallback();
            }
            this.removeDirty(this.dirtyId);
            this.actionHidden();
            this.resetEditingResourceUuid();
            this.setEditingResourceUuid(this.params.parentQuestionUuid);
            if (isDefined(parentComponentId)) {
              this.setDirty(parentComponentId);
            }
            this.store.clearSelectionList();
            this.store.clearQuestionPartsSelectionList();
          },
          (error) => {
            if (error && error[0] && error[0] === "question.mpq.max.index") {
              this.showMPQMaxChildQuestion();
            }
            if (isDefined(data) && typeof data.onFailCallback === "function") {
              data.onFailCallback();
            }
          },
        )
        .finally(() => {
          this.isProcessing.data = false;
        });
    },
    handleSaveMediaObject(data) {
      const requestData = {
        contextType: "LESSON",
        contextUuid: this.lesson && this.lesson.uuid,
        uploadFrom: "question",
        uuid: this.getSelectionByUuid(),
      };
      MediaObjectApi.duplicateMediaObject(JSON.stringify(requestData))
        .then(
          (duplicatedMoData) => {
            if (isDefined(data) && typeof data.onSuccessCallback === "function") {
              data.onSuccessCallback();
            }
            this.removeDirty(this.dirtyId);
            this.actionHidden();
            if (isNull(duplicatedMoData.data)) {
              const mediaData = this.getFilteredSelection().map((item) => ({
                ...item,
                isAddNewMedia: data?.isAddNewMedia ?? false,
              }));

              this.$emit("onMediaSelected", mediaData);
            } else {
              const mediaData = {
                resourceDomain: duplicatedMoData.data.resourceDomain,
                type: duplicatedMoData.data.fileType,
                uuid: duplicatedMoData.data.uuid,
                title: duplicatedMoData.data.title,
                description: duplicatedMoData.data.description,
                isAddNewMedia: data?.isAddNewMedia ?? false,
              };
              this.$emit("onMediaSelected", [mediaData]);
            }
            this.store.clearSelectionList();
          },
          () => {
            if (isDefined(data) && typeof data.onFailCallback === "function") {
              data.onFailCallback();
            }
          },
        )
        .finally(() => {
          this.isProcessing.data = false;
        });
    },
    showMaximumUploadModal() {
      const props = {
        title: i18n.t("tinymce_plugin_media_upload_modal_title"),
        content: i18n.t("tinymce_plugin_media_max_upload_modal_message"),
        primaryBtnLabel: i18n.t("button_ok"),
        use_secondaryButton: false,
        actionPrimary: () => {
          this.isProcessing.data = false;
        },
        actionHidden: () => {
          this.isProcessing.data = false;
        },
        actionHideRequest: () => {
          this.isProcessing.data = false;
        },
      };
      this.$modal.showMessage("warning", props);
    },
    async handleSaveThumbnail(data) {
      const params = {
        imageType: "thumbnail",
      };

      let resourceType;
      let identifier;
      if (this.currentPageId === "sectionCover") {
        resourceType = THUMBNAIL_RESOURCE_TYPE.SECTION;
        identifier = this.currentSection.id;
      }
      if (this.currentPageId === "cover") {
        resourceType = THUMBNAIL_RESOURCE_TYPE.LESSON;
        identifier = this.lesson.uuid;
      }

      const imageData = this.getFilteredSelection();

      if (imageData[0].resourceDomain === RESOURCE_DOMAIN.COMMUNITY) {
        params.mediaObjectUuid = this.getSelectionByUuid();
        this.handleAddThumbnail(data, params, resourceType, identifier);
      } else {
        const blob = await MediaObjectApi.getMoImageBlob(imageData[0].dataSrc);
        const fileExt = imageData[0].dataSrc.split(/[#?]/)[0].split(".").pop().trim();
        const file = new File([blob], `${imageData[0].title}.${fileExt}`, {
          type: blob.type,
        });

        const FileUploaderModal = defineAsyncComponent(
          () => import("@plugins/file-uploader-v2/components/FileUploaderModal.vue"),
        );

        this.actionHidden();
        this.modalId = this.$modal.show(
          FileUploaderModal,
          {
            uploadFlow: UploadFlow.IMG,
            addedFile: file,
            croppingConfiguration: {
              kind: CroppingToolKind.cropping_tool_3.key,
              exportSize: Dimensions.medium,
              initialAspectRatio: 3 / 2,
            },
          },
          {
            "update:fileEntry": (uploadedResult) => {
              this.handleUploadThumbnail(data, uploadedResult, resourceType, identifier);
            },
          },
        );
      }
    },
    async handleUploadThumbnail(data, fileResponse, resourceType, identifier) {
      if (fileResponse?.status === FileStatus.READY) {
        const fileEntry = {
          fileUuid: fileResponse.uuid,
          filename: fileResponse.name,
        };
        this.uploadThumbnail({
          identifier: identifier,
          resourceType: resourceType,
          params: fileEntry,
        })
          .then(
            () => {
              if (isDefined(data) && typeof data.onSuccessCallback === "function") {
                data.onSuccessCallback();
              }
              this.removeDirty(this.dirtyId);
              this.actionHidden();
              this.store.clearSelectionList();
            },
            () => {
              if (isDefined(data) && typeof data.onFailCallback === "function") {
                data.onFailCallback();
              }
            },
          )
          .finally(() => {
            this.isProcessing.data = false;
            this.$modal.remove(this.modalId);
          });
      }
    },
    handleAddThumbnail(data, params, resourceType, identifier) {
      this.addThumbnail({
        identifier: identifier,
        resourceType: resourceType,
        params: params,
      })
        .then(
          () => {
            if (isDefined(data) && typeof data.onSuccessCallback === "function") {
              data.onSuccessCallback();
            }
            this.removeDirty(this.dirtyId);
            this.actionHidden();
            this.store.clearSelectionList();
          },
          () => {
            if (isDefined(data) && typeof data.onFailCallback === "function") {
              data.onFailCallback();
            }
          },
        )
        .finally(() => {
          this.isProcessing.data = false;
        });
    },
    createNewActivity() {
      const activityList = this.getActivityList(this.currentSectionId);
      const activityListLength = activityList.length;
      const targetPageIdx = isDefined(this.params.targetPageIdx)
        ? this.params.targetPageIdx
        : activityListLength + 1;
      let quizAlpStageId = 0;
      let courseCreationOption = COURSE_CREATION_OPTION.ACTIVITY;
      if (this.params.resourceType === ADD_COMPONENT_TYPE.QUIZ) {
        quizAlpStageId = this.getQuizAlpStageId;
        courseCreationOption = COURSE_CREATION_OPTION.CREATE_BLANK_QUIZ;
      }
      return this.createNewSectionActivity({
        sectionId: this.params.sectionId,
        alpStageId: this.params.resourceType === ADD_COMPONENT_TYPE.QUIZ ? quizAlpStageId : 0,
        targetPageIdx: targetPageIdx,
        courseCreationOption: courseCreationOption,
      });
    },
    handleSaveActivities(emitEvent) {
      const data = emitEvent.data;
      if (!this.validateMaximumActivities(data)) {
        if (emitEvent && typeof emitEvent.onFailCallback === "function") {
          emitEvent.onFailCallback(true); // true = validation error
        }
        this.isProcessing.data = false;
        return;
      }
      LessonApi.checkHasAnyProcessingInActivity(data.lessonActivityId).then((response) => {
        if (response.code !== "ok") {
          this.passiveApiFailHandler();
          return;
        }
        if (response.data) {
          //has something that is still processing
          const props = {
            title: this.$i18n.t("assignment_header_right_unable_to_copy_to_drive_title"),
            content: this.$i18n.t(
              "assignment_header_right_unable_to_copy_to_drive_processing_text",
            ),
            primaryBtnLabel: this.$i18n.t("button_ok"),
            use_secondaryButton: false,
          };
          this.$modal.showMessage("warning", props);
          this.isProcessing.data = false;
          if (emitEvent && typeof emitEvent.onFailCallback === "function") {
            emitEvent.onFailCallback(false); // false = generic error
          }
        } else {
          this.handleCopy(emitEvent, data);
        }
      });
    },
    handleCopy(emitEvent, data) {
      let actionPromise;
      if (this.params.sectionId) {
        actionPromise = this.copyPagesToSection(this.populateCopyPagesRequestData(data));
      } else {
        actionPromise = this.copyPages(this.populateCopyPagesRequestData(data));
      }
      actionPromise.then(
        () => {
          if (emitEvent && typeof emitEvent.onSuccessCallback === "function") {
            emitEvent.onSuccessCallback();
          }
          this.handlePostSaveActivities();
        },
        () => {
          if (emitEvent && typeof emitEvent.onFailCallback === "function") {
            emitEvent.onFailCallback(false); // false = backend error
          }
          this.passiveApiFailHandler();
        },
      );
    },
    handlePostSaveActivities() {
      const updatedActivityLists = this.getActivityList(this.params.sectionId);
      const nextTargetPage = updatedActivityLists.find((activity) => {
        return activity.pageNo === this.params.targetPageIdx;
      });

      // route to first newly added activities
      this.$emit("activityAdded", {
        newActivityId: nextTargetPage.id,
        selectedActivityType: ACTIVITY_TYPE_SELECTION.FROM_EXISTING,
      });
      this.$nextTick(() => {
        this.actionBack();
      });
    },
    populateCopyPagesRequestData(data) {
      return {
        targetLessonUuid: this.lesson.uuid,
        lessonUuid: data.lessonUuid, // source lesson uuid
        lessonStatus: this.lesson.status, // target lesson status
        lessonActivityId: data.lessonActivityId, // list of selected activity ids
        targetPageId: this.params.selectedPageId, // selected activity id from target lesson
        targetSectionId: this.params.sectionId,
        targetPageIndex: this.params.targetPageIdx,
        remainTitle: true,
      };
    },
    validateMaximumActivities(data) {
      if (
        data.lessonActivityId.length + this.params.totalExistingActivities >
        VALIDATION.MAX_ACTIVITIES
      ) {
        return false;
      }
      return true;
    },
    handleUpdateActivityTitle(title, pageId) {
      this.saveQuizActivityTitle({
        newTitle: title,
        pageId: pageId,
      });
    },
    handleSelectGameBook(gameBookId) {
      this.isProcessing.data = false;
      this.removeDirty(this.dirtyId);
      this.$emit("selectionSaved", gameBookId);
    },
  },
};
